import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { FrozenColumnScroll } from '../layout/FrozenColumnScroll';
import { colors, spacing, typography } from '../../core/theme/tokens';
import type { NetWorthYearRow } from '../../types/financial';
import { formatK } from './d3/format';

type NetWorthYearTableProps = {
  rows: NetWorthYearRow[];
  maxRows?: number;
};

const YEAR_COL_WIDTH = 56;
const MONEY_COL_WIDTH = 88;

function totalAssets(row: NetWorthYearRow): number {
  const { home, car, retirement, brokerage, cash, other } = row.assets;
  return home + car + retirement + brokerage + cash + other;
}

function totalLiabilities(row: NetWorthYearRow): number {
  const { mortgage, auto, creditCards, other } = row.liabilities;
  return mortgage + auto + creditCards + other;
}

export function NetWorthYearTable({ rows, maxRows }: NetWorthYearTableProps) {
  const displayRows = maxRows ? rows.slice(0, maxRows) : rows;

  return (
    <FrozenColumnScroll
      frozenWidth={YEAR_COL_WIDTH}
      frozen={
        <View>
          <View style={styles.headerRow}>
            <View style={[styles.cell, { width: YEAR_COL_WIDTH }]}>
              <Text style={styles.headerText} numberOfLines={1}>
                Year
              </Text>
            </View>
          </View>
          {displayRows.map((row) => (
            <View key={row.year} style={styles.dataRow}>
              <View style={[styles.cell, styles.yearCell, { width: YEAR_COL_WIDTH }]}>
                <Text style={[styles.cellText, styles.yearText]} numberOfLines={1}>
                  {row.year}
                </Text>
              </View>
            </View>
          ))}
        </View>
      }
      scrollable={
        <View>
          <View style={styles.headerRow}>
            <View style={[styles.cell, { width: MONEY_COL_WIDTH }]}>
              <Text style={styles.headerText} numberOfLines={1}>
                Assets
              </Text>
            </View>
            <View style={[styles.cell, { width: MONEY_COL_WIDTH }]}>
              <Text style={styles.headerText} numberOfLines={1}>
                Debt
              </Text>
            </View>
            <View style={[styles.cell, styles.highlightHeader, { width: MONEY_COL_WIDTH }]}>
              <Text style={[styles.headerText, styles.highlightHeaderText]} numberOfLines={1}>
                Net worth
              </Text>
            </View>
          </View>
          {displayRows.map((row) => (
            <View key={row.year} style={styles.dataRow}>
              <View style={[styles.cell, { width: MONEY_COL_WIDTH }]}>
                <Text style={styles.cellText} numberOfLines={1}>
                  {formatK(totalAssets(row))}
                </Text>
              </View>
              <View style={[styles.cell, { width: MONEY_COL_WIDTH }]}>
                <Text style={styles.cellText} numberOfLines={1}>
                  {formatK(totalLiabilities(row))}
                </Text>
              </View>
              <View style={[styles.cell, styles.highlightCell, { width: MONEY_COL_WIDTH }]}>
                <Text
                  style={[
                    styles.cellText,
                    styles.highlightText,
                    { color: row.netWorth >= 0 ? colors.accent : colors.danger },
                  ]}
                  numberOfLines={1}
                >
                  {formatK(row.netWorth)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      }
    />
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.surfaceElevated,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  cell: {
    paddingHorizontal: spacing.xs,
    justifyContent: 'center',
  },
  yearCell: {
    backgroundColor: colors.surface,
  },
  highlightCell: {
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
  },
  highlightHeader: {
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
  },
  headerText: {
    ...typography.label,
    color: colors.textMuted,
    fontSize: 10,
  },
  highlightHeaderText: {
    color: colors.accent,
  },
  cellText: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
    fontSize: 12,
  },
  yearText: {
    color: colors.text,
    fontWeight: '600',
  },
  highlightText: {
    fontWeight: '700',
  },
});
